(() => {
  const GOOGLE_MAPS_PROMISE_KEY = '__seapuriGoogleMapsPromise';

  const normalize = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  const escapeHtml = (value) =>
    String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  const loadGoogleMaps = (apiKey) => {
    if (window.google?.maps) return Promise.resolve(window.google.maps);
    if (window[GOOGLE_MAPS_PROMISE_KEY]) return window[GOOGLE_MAPS_PROMISE_KEY];

    window[GOOGLE_MAPS_PROMISE_KEY] = new Promise((resolve, reject) => {
      const callbackName = `seapuriStoreLocatorReady${Date.now()}`;
      const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
      let timeoutId;

      const finish = () => {
        window.clearTimeout(timeoutId);
        delete window[callbackName];
        if (window.google?.maps) {
          resolve(window.google.maps);
        } else {
          reject(new Error('Google Maps did not initialize.'));
        }
      };

      timeoutId = window.setTimeout(() => {
        delete window[callbackName];
        reject(new Error('Google Maps loading timed out.'));
      }, 15000);

      if (existingScript) {
        existingScript.addEventListener('load', finish, { once: true });
        existingScript.addEventListener('error', () => reject(new Error('Google Maps failed to load.')), {
          once: true
        });
        return;
      }

      window[callbackName] = finish;

      const script = document.createElement('script');
      const params = new URLSearchParams({
        key: apiKey,
        loading: 'async',
        callback: callbackName,
        v: 'weekly'
      });

      script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        window.clearTimeout(timeoutId);
        delete window[callbackName];
        reject(new Error('Google Maps failed to load.'));
      };
      document.head.appendChild(script);
    });

    return window[GOOGLE_MAPS_PROMISE_KEY];
  };

  if (customElements.get('seapuri-store-locator')) return;

  customElements.define(
    'seapuri-store-locator',
    class SeapuriStoreLocator extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;
        this.dataset.initialized = 'true';

        this.searchForm = this.querySelector('[data-store-search-form]');
        this.searchInput = this.querySelector('[data-store-search-input]');
        this.searchSubmit = this.querySelector('[data-store-search-submit]');
        this.searchClear = this.querySelector('[data-store-search-clear]');
        this.resultsStatus = this.querySelector('[data-store-results-status]');
        this.noResults = this.querySelector('[data-store-no-results]');
        this.mapElement = this.querySelector('[data-store-map]');
        this.mapFallback = this.querySelector('[data-store-map-fallback]');
        this.mapState = this.querySelector('[data-store-map-state]');
        this.regionElements = Array.from(
          this.querySelectorAll('.seapuri-store-locator__regions > [data-store-region]')
        );
        this.regionOpenStates = new Map(
          this.regionElements.map((region) => {
            const details = region.querySelector('details');
            return [region, Boolean(details?.open)];
          })
        );
        this.storeRecords = this.collectStores();
        this.markers = [];

        this.bindEvents();
        this.applyFilter('');
        this.initializeMap();
      }

      collectStores() {
        return Array.from(this.querySelectorAll('[data-store-result]')).map((button) => {
          const latitude = Number.parseFloat(button.dataset.storeLatitude);
          const longitude = Number.parseFloat(button.dataset.storeLongitude);

          return {
            id: button.dataset.storeId,
            name: button.dataset.storeName || '',
            address: button.dataset.storeAddress || '',
            region: button.dataset.storeRegion || '',
            retailerUrl: button.dataset.storeRetailerUrl || '',
            position:
              Number.isFinite(latitude) && Number.isFinite(longitude)
                ? { lat: latitude, lng: longitude }
                : null,
            button,
            resultItem: button.closest('[data-store-result-item]'),
            regionItems: Array.from(this.querySelectorAll(`[data-region-store][data-store-id="${button.dataset.storeId}"]`)),
            marker: null,
            matches: true
          };
        });
      }

      bindEvents() {
        this.searchForm?.addEventListener('submit', (event) => {
          event.preventDefault();
          this.applyFilter(this.searchInput?.value || '');
        });

        this.searchForm?.addEventListener('reset', () => {
          window.requestAnimationFrame(() => {
            this.applyFilter('');
            this.searchInput?.focus();
          });
        });

        this.searchInput?.addEventListener('input', (event) => {
          this.applyFilter(event.currentTarget.value);
        });

        this.storeRecords.forEach((record) => {
          record.button.addEventListener('click', () => this.focusStore(record));
        });
      }

      async initializeMap() {
        const apiKey = this.dataset.googleMapsApiKey;
        if (!this.mapElement) return;

        if (!apiKey) {
          this.updateFallbackMap(this.storeRecords.find((record) => record.address || record.position));
          return;
        }

        try {
          await loadGoogleMaps(apiKey);
          await this.geocodeMissingStorePositions();

          this.map = new google.maps.Map(this.mapElement, {
            center: { lat: 0, lng: 0 },
            zoom: 2,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true
          });
          this.infoWindow = new google.maps.InfoWindow();

          this.storeRecords.forEach((record) => {
            if (!record.position) return;

            record.marker = new google.maps.Marker({
              map: this.map,
              position: record.position,
              title: record.name
            });
            record.marker.addListener('click', () => this.focusStore(record));
            this.markers.push(record.marker);
          });

          if (this.markers.length > 0) {
            this.mapState.hidden = true;
            this.fitVisibleMarkers();
          } else {
            this.showMapState('Add latitude and longitude to Store blocks to display map markers.');
          }
        } catch (error) {
          console.error(error);
          this.showMapState('The map could not be loaded. Check the Google Maps API key and restrictions.');
        }
      }

      async geocodeMissingStorePositions() {
        const recordsToGeocode = this.storeRecords.filter((record) => !record.position && record.address);
        if (recordsToGeocode.length === 0) return;

        const geocoder = new google.maps.Geocoder();

        for (const record of recordsToGeocode) {
          try {
            const response = await geocoder.geocode({ address: record.address });
            const location = response.results?.[0]?.geometry?.location;
            if (!location) continue;

            record.position = { lat: location.lat(), lng: location.lng() };
          } catch (error) {
            console.warn(`Store address could not be located: ${record.name}`, error);
          }
        }
      }

      applyFilter(value) {
        const query = normalize(value);
        const terms = query.split(/\s+/).filter(Boolean);
        let matchCount = 0;

        this.storeRecords.forEach((record) => {
          const haystack = normalize([record.name, record.address, record.region].join(' '));
          record.matches = terms.length === 0 || terms.every((term) => haystack.includes(term));

          if (record.resultItem) record.resultItem.hidden = !record.matches;
          record.regionItems.forEach((item) => {
            item.hidden = !record.matches;
          });
          if (record.marker) record.marker.setMap(record.matches ? this.map : null);
          if (record.matches) matchCount += 1;
        });

        this.regionElements.forEach((regionElement) => {
          const regionStores = Array.from(regionElement.querySelectorAll('[data-region-store]'));
          const hasVisibleStore = regionStores.some((item) => !item.hidden);
          const details = regionElement.querySelector('details');

          regionElement.hidden = terms.length > 0 && !hasVisibleStore;
          if (details && terms.length > 0 && hasVisibleStore) {
            details.open = true;
          } else if (details && terms.length === 0) {
            details.open = this.regionOpenStates.get(regionElement) || false;
          }
        });

        if (this.resultsStatus) {
          this.resultsStatus.textContent = `${matchCount} ${matchCount === 1 ? 'location' : 'locations'}`;
        }
        if (this.noResults) {
          this.noResults.hidden = matchCount !== 0;
          if (matchCount === 0) this.noResults.textContent = this.dataset.noResultsText;
        }
        if (this.searchSubmit) this.searchSubmit.hidden = query.length > 0;
        if (this.searchClear) this.searchClear.hidden = query.length === 0;

        this.storeRecords.forEach((record) => {
          record.button.removeAttribute('aria-current');
        });

        if (this.map) {
          this.fitVisibleMarkers();
        } else {
          this.updateFallbackMap(this.storeRecords.find((record) => record.matches && (record.address || record.position)));
        }
      }

      fitVisibleMarkers() {
        const visibleRecords = this.storeRecords.filter(
          (record) => record.matches && record.marker && record.marker.getMap()
        );

        if (visibleRecords.length === 0) {
          this.infoWindow?.close();
          return;
        }

        if (visibleRecords.length === 1) {
          this.map.setCenter(visibleRecords[0].position);
          this.map.setZoom(12);
          return;
        }

        const bounds = new google.maps.LatLngBounds();
        visibleRecords.forEach((record) => bounds.extend(record.position));
        this.map.fitBounds(bounds, 48);
      }

      focusStore(record) {
        this.storeRecords.forEach((store) => {
          store.button.toggleAttribute('aria-current', store.id === record.id);
        });

        if (!record.marker || !this.map) {
          this.updateFallbackMap(record);
          this.scrollMapIntoView();
          return;
        }

        record.marker.setMap(this.map);
        this.map.panTo(record.position);
        this.map.setZoom(Math.max(this.map.getZoom() || 0, 12));
        this.openMarker(record);

        this.scrollMapIntoView();
      }

      scrollMapIntoView() {
        if (!window.matchMedia('(max-width: 749px)').matches) return;
        this.mapElement?.closest('.seapuri-store-locator__map-panel')?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }

      updateFallbackMap(record) {
        if (!this.mapFallback || !record) return;

        const query = record.position ? `${record.position.lat},${record.position.lng}` : record.address;
        if (!query || this.mapFallback.dataset.storeMapQuery === query) return;

        const params = new URLSearchParams({ q: query, z: '14', output: 'embed' });
        this.mapFallback.src = `https://maps.google.com/maps?${params.toString()}`;
        this.mapFallback.dataset.storeMapQuery = query;
      }

      openMarker(record) {
        if (!record.marker || !this.infoWindow || !this.map) return;

        const links = [];
        if (record.retailerUrl) {
          links.push(
            `<a href="${escapeHtml(record.retailerUrl)}" target="_blank" rel="noopener">Visit retailer</a>`
          );
        }

        this.infoWindow.setContent(`
          <div class="seapuri-store-locator__map-card">
            <strong>${escapeHtml(record.name)}</strong>
            ${record.address ? `<p>${escapeHtml(record.address)}</p>` : ''}
            ${links.length ? `<div class="seapuri-store-locator__map-card-links">${links.join('')}</div>` : ''}
          </div>
        `);
        this.infoWindow.open({ map: this.map, anchor: record.marker });
      }

      showMapState(message) {
        if (!this.mapState) return;
        this.mapState.textContent = message;
        this.mapState.hidden = false;
      }
    }
  );
})();
